import type { ColDef } from 'ag-grid-community'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { getPositionLabel } from '../utils/positionLabel'
import type { AttendancePlayer } from '../types'
import AttendanceSortHeader, {
  REGROUP_SORT_KEY,
  type AttendanceSortHeaderParams,
} from '../components/cells/AttendanceSortHeader'
import AttendanceTooltipHeader, {
  type AttendanceTooltipHeaderParams,
} from '../components/cells/AttendanceTooltipHeader'
import { ATTENDANCE_STATUS_LABELS } from '@sportninja/common/features/game/attendance/constants'
import {
  ATTENDING_HEADER_TOOLTIP,
  AVAILABLE_HEADER_TOOLTIP,
  CONFIRM_ATTENDING_HEADER_TOOLTIP,
} from '../utils/statusCopy'
import AvailabilityCell from '../components/cells/AvailabilityCell'
import ConfirmCell from '../components/cells/ConfirmCell'
import JerseyCell from '../components/cells/JerseyCell'
import NameCell from '../components/cells/NameCell'
import PromoteCell from '../components/cells/PromoteCell'
import SpareStatusCell from '../components/cells/SpareStatusCell'
import StartingCell from '../components/cells/StartingCell'

// Cell classes styled by AttendanceSection's grid wrapper.
export const ATTENDANCE_CELL_CLASS = 'attendance-cell'
export const ATTENDANCE_CENTER_CLASS = 'attendance-cell-center'

// Clicking these (non-interactive) columns counts as pressing the row. The
// other columns hold controls whose clicks must not.
export const ROW_PRESS_COLUMN_IDS = ['position', 'name', 'status']

// Wide enough for the "Confirm Attending" header on one line.
const CONFIRM_ATTENDING_MIN_WIDTH = 168

export interface UseAttendanceColumnsParams {
  isSpareCandidate: boolean
  hideConfirm: boolean
  // With the confirm column hidden, keep its space as an empty column so this
  // grid lines up with a sibling section that shows it.
  reserveConfirmColumn: boolean
  // Roster Spares asks whether spares are available; Roster whether players
  // are attending.
  isRosterSpares: boolean
  showStartingColumn: boolean
  startingLabel: string
  // Header tooltip for the starting column (it proposes the starter).
  startingTooltip: string
}

const sortHeader = (
  sortKey: string,
  tooltip?: string
): Pick<
  ColDef<AttendancePlayer>,
  'headerComponent' | 'headerComponentParams'
> => {
  const headerComponentParams: AttendanceSortHeaderParams = { sortKey, tooltip }
  return { headerComponent: AttendanceSortHeader, headerComponentParams }
}

// A plain (non-sortable) header with an explanation on hover.
const tooltipHeader = (
  tooltip: string
): Pick<
  ColDef<AttendancePlayer>,
  'headerComponent' | 'headerComponentParams'
> => {
  const headerComponentParams: AttendanceTooltipHeaderParams = { tooltip }
  return { headerComponent: AttendanceTooltipHeader, headerComponentParams }
}

const centered: Pick<ColDef<AttendancePlayer>, 'cellClass' | 'headerClass'> = {
  cellClass: [ATTENDANCE_CELL_CLASS, ATTENDANCE_CENTER_CLASS],
  headerClass: ATTENDANCE_CENTER_CLASS,
}

/** Column set for one attendance section, shaped by the section's flags. */
export const useAttendanceColumns = ({
  isSpareCandidate,
  hideConfirm,
  reserveConfirmColumn,
  isRosterSpares,
  showStartingColumn,
  startingLabel,
  startingTooltip,
}: UseAttendanceColumnsParams): ColDef<AttendancePlayer>[] => {
  'use memo'

  // Every column takes a share of the page width (`flex`) instead of leaving it
  // all to Name; `minWidth` is the width each one needs before the grid starts
  // scrolling sideways on a narrow window.
  const columns: ColDef<AttendancePlayer>[] = []

  if (!isSpareCandidate) {
    columns.push({
      colId: 'number',
      headerName: '#',
      flex: 1,
      minWidth: 88,
      cellRenderer: JerseyCell,
      ...sortHeader('number'),
    })
  }

  columns.push(
    {
      colId: 'name',
      headerName: capitalizeWords('name'),
      flex: 3,
      minWidth: 200,
      cellRenderer: NameCell,
      ...sortHeader('name'),
    },
    {
      colId: 'position',
      headerName: capitalizeWords('position'),
      flex: 1,
      // Fits the full names ("Goalkeeper", "Midfielder") on one line.
      minWidth: 120,
      valueGetter: ({ data }) => getPositionLabel(data),
      ...sortHeader('position'),
    }
  )

  if (showStartingColumn) {
    columns.push({
      colId: 'starting',
      headerName: capitalizeWords(startingLabel),
      // Holds a single radio, so it stays at its header's width rather than
      // taking a flex share of the page.
      width: 140,
      cellRenderer: StartingCell,
      ...centered,
      ...tooltipHeader(startingTooltip),
    })
  }

  columns.push(
    isSpareCandidate
      ? {
          colId: 'status',
          headerName: capitalizeWords('status'),
          flex: 2,
          minWidth: 128,
          cellRenderer: SpareStatusCell,
          ...centered,
        }
      : {
          colId: 'available',
          headerName: capitalizeWords(
            ATTENDANCE_STATUS_LABELS[isRosterSpares ? 'spare' : 'roster']
              .attending
          ),
          flex: 2,
          minWidth: 152,
          cellRenderer: AvailabilityCell,
          ...centered,
          ...sortHeader(
            REGROUP_SORT_KEY,
            isRosterSpares ? AVAILABLE_HEADER_TOOLTIP : ATTENDING_HEADER_TOOLTIP
          ),
        }
  )

  if (!hideConfirm) {
    columns.push({
      colId: 'confirm',
      headerName: capitalizeWords(
        isSpareCandidate ? 'promote' : 'confirm attending'
      ),
      flex: 2,
      minWidth: isSpareCandidate ? 120 : CONFIRM_ATTENDING_MIN_WIDTH,
      cellRenderer: isSpareCandidate ? PromoteCell : ConfirmCell,
      ...centered,
      ...(isSpareCandidate
        ? {}
        : tooltipHeader(CONFIRM_ATTENDING_HEADER_TOOLTIP)),
    })
  } else if (reserveConfirmColumn) {
    columns.push({
      colId: 'confirm-spacer',
      headerName: '',
      flex: 2,
      minWidth: CONFIRM_ATTENDING_MIN_WIDTH,
    })
  }

  return columns
}
